import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';
import {
  getDefaultRouteForRole,
  InternalRole,
  INTERNAL_ROLES,
  ROLE_LABELS,
} from '../config/role-routes';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();
  const { showError, showSuccess } = useToast();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [showMFA, setShowMFA] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');

  useEffect(() => {
    if (isAuthenticated) {
      const from = (location.state as any)?.from?.pathname;
      const target = from ?? '/gestion/dashboard';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  useEffect(() => {
    const remembered = authService.getRememberedEmail();
    if (remembered) {
      setEmail(remembered);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.login(email.trim(), password);

      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        setShowMFA(true);
        setIsLoading(false);
        return;
      }

      if (result.success && result.data) {
        if (!INTERNAL_ROLES.includes(result.data.user.role as InternalRole)) {
          authService.clearSession();
          setError('Este usuario no tiene acceso a la PWA interna.');
          setIsLoading(false);
          return;
        }

        if (rememberMe) authService.setRememberedEmail(email.trim());
        else authService.removeRememberedEmail();

        login(result.data.user, result.data.accessToken);
        showSuccess(`Bienvenido, ${result.data.user.name}`);

        const redirect = getDefaultRouteForRole(result.data.user.role);
        navigate(redirect, { replace: true });
      }
    } catch (err: any) {
      const msg = err?.message || 'Credenciales inválidas';
      setError(msg);
      showError(msg);
      setIsLoading(false);
    }
  };

  const handleMFAVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const result = await authService.verifyMFA(mfaToken, mfaCode.trim());

      if (result.success && result.data) {
        if (!INTERNAL_ROLES.includes(result.data.user.role as InternalRole)) {
          authService.clearSession();
          setError('Este usuario no tiene acceso a la PWA interna.');
          setIsLoading(false);
          return;
        }

        if (rememberMe) authService.setRememberedEmail(email.trim());
        login(result.data.user, result.data.accessToken);
        showSuccess(`Bienvenido, ${result.data.user.name}`);
        navigate(getDefaultRouteForRole(result.data.user.role), { replace: true });
      }
    } catch (err: any) {
      const msg = err?.message || 'Código inválido';
      setError(msg);
      showError(msg);
      setIsLoading(false);
    }
  };

  if (showMFA) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 px-5 safe-top">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-6">
          <div className="text-center mb-6">
            <div className="text-4xl mb-2">🔐</div>
            <h1 className="text-xl font-bold text-slate-900">Verificación en dos pasos</h1>
            <p className="text-sm text-slate-500 mt-1">
              Ingresa el código de 6 dígitos de tu app autenticadora
            </p>
          </div>

          <form onSubmit={handleMFAVerify} className="space-y-4">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              required
              autoFocus
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full text-center text-2xl tracking-[0.5em] font-mono py-3 border-2 border-slate-200 rounded-lg focus:border-primary focus:outline-none"
            />

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading || mfaCode.length !== 6}
              className="w-full py-3 rounded-lg bg-primary text-white font-semibold disabled:opacity-50"
            >
              {isLoading ? 'Verificando…' : 'Verificar código'}
            </button>

            <button
              type="button"
              onClick={() => {
                setShowMFA(false);
                setMfaCode('');
                setMfaToken('');
                setError('');
                setIsLoading(false);
              }}
              className="w-full text-sm text-slate-500 py-1"
            >
              ← Volver al inicio de sesión
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-900 px-5 safe-top">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary text-white text-2xl font-bold mb-3">
            GA
          </div>
          <h1 className="text-2xl font-bold text-white">Gran Arquitectura</h1>
          <p className="text-sm text-white/60 mt-1">Portal interno del despacho</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl p-6 space-y-4">
          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-slate-700 mb-1">
              Correo electrónico
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="tu@despacho.com"
              disabled={isLoading}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10"
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-semibold text-slate-700 mb-1">
              Contraseña
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              disabled={isLoading}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/10"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              disabled={isLoading}
              className="w-4 h-4 accent-primary"
            />
            Recordarme
          </label>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-3 py-2">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-3 rounded-lg bg-primary text-white font-semibold disabled:opacity-50 active:bg-primary-dark transition-colors"
          >
            {isLoading ? 'Iniciando sesión…' : 'Iniciar sesión'}
          </button>
        </form>

        <p className="text-center text-xs text-white/40 mt-6">
          {Object.values(ROLE_LABELS).join(' · ')}
        </p>
      </div>
    </div>
  );
}